import { CustomDataDefItem } from "../interfaces/CustomDataDefItem";
import { CustomDataPlainListItem } from "../interfaces/CustomDataPlainList";
import { ExtensionItem } from "../interfaces/ExtensionItem";
import { FunctionItem } from "../interfaces/FunctionItem";
import { GroupedData } from "../interfaces/GroupedData";
import { ManifestItem } from "../interfaces/ManifestItem";

export interface UserInfoItem {
    field: string;
    label: string[];
    store?: string;
    value?: string;
    item: string;
    subItem?: string;
    hidden: boolean;
    mask?: string;
}

export interface UserInfoCategory {
    category: string[];
    items: UserInfoItem[];
}

export interface UserInfoExtended extends UserInfoItem {
    key: string,
    labelSwe: string,
    labelEng: string,
}

export interface UserPrivilegesItem {
    isUser: boolean;
    isRedirected: boolean;
    isChangeTenant: boolean;
    isAdmin: boolean;
    isSettingsAdmin: boolean;
    isContributorAdmin: boolean;
    isTemplateAdmin: boolean;
    isPictureAdmin: boolean;
    isIconAdmin: boolean;
    isLogoAdmin: boolean;
    isSlideAdmin: boolean;
    isContentAdmin: boolean;
    isEmailSignatureAdmin: boolean;
    isBuilderAdmin: boolean;
    isAcademyAdmin: boolean;
    isExcel2WebAdmin: boolean;
    isExcel2WordAdmin: boolean;
    isRehngruppen: boolean;
}

export default interface SettingsCompany {
    Domain: string
    CompanyName: string
    Caption: string[]
    hideLogo: boolean
    hideCaption: boolean
    logoHeight: number
    UserInformation: UserInfoCategory[] | null
    LogoBase64: String
    Extensions: ExtensionItem[]
    Functions: FunctionItem[]
    Manifest: ManifestItem[]
    ManifestOutlook: ManifestItem[]
    ManifestOutlookClassic: boolean
    ManifestExcel2: ManifestItem[]
    RibbonIcon16: string
    RibbonIcon32: string
    RibbonIcon80: string
    RibbonOutlookIcon16: string
    RibbonOutlookIcon32: string
    RibbonOutlookIcon80: string
    enableTestDomain: boolean
    enableRedirect: boolean
    UniqueManifestID: boolean
    InternalUse: boolean
    UserPrivileges?: UserPrivilegesItem
    UserData?: UserInfoItem[]
    GroupedData?: GroupedData[]
    CustomDataPlain?: CustomDataPlainListItem[]
    CustomDataDef?: CustomDataDefItem
    CustomDataPlainSelector?: any[]
    CustomDataTreeSelector?: any[]
}

export const exampleInfoList: UserInfoExtended[] = [{
    field: 'No items',
    hidden: false,
    item: '',
    key: '0',
    label: [''],
    labelEng: '',
    labelSwe: '',
    store: '',
}]

export function GetUseDynamicForms(settings: SettingsCompany): boolean {
    if (!settings.Functions) { return false }
    return settings.Functions.find(item => item.FunctionName === 'Dynamic Forms')?.EnableFunction!;
}

export function GetUseUserInformation(settings: SettingsCompany): boolean {
    if (!settings.Functions) { return false }
    return settings.Functions.find(item => item.FunctionName === 'User Information')?.EnableFunction!;
}

export function GetUseSharePointLinks(settings: SettingsCompany): boolean {
    if (!settings.Functions) { return false }
    return settings.Functions.find(item => item.FunctionName === 'SharePoint Links')?.EnableFunction!;
}

export function GetUse365Groups(settings: SettingsCompany): boolean {
    if (!settings.Functions) { return false }
    return settings.Functions.find(item => item.FunctionName === 'Microsoft 365 Groups')?.EnableFunction!;
}

export function GetUseUserDataGroups(settings: SettingsCompany): boolean {
    if (!settings.Functions) { return false }
    return settings.Functions.find(item => item.FunctionName === 'User Data Groups')?.EnableFunction!;
}

export function GetUseAnyGroups(settings: SettingsCompany): boolean {
    return GetUse365Groups(settings) || GetUseUserDataGroups(settings)
}

export function GetUseCustomData(settings: SettingsCompany): boolean {
    if (!settings.Functions) { return false }
    return settings.Functions.find(item => item.FunctionName === 'Custom Data')?.EnableFunction!;
}

export function GetUseCustomDataInEmailSignatures(settings: SettingsCompany): boolean {
    if (!GetUseCustomData(settings)) { return false }
    return settings.Functions.find(item => item.FunctionName === 'Custom Data')?.ExtraFields?.find(item => item.Title === 'Use in Email Signatures')?.Value?.toLowerCase() === 'true';
}

export function GetMaxPictureCount(settings: SettingsCompany): number {
    if (!settings.Extensions) { return 250 }
    return parseInt(settings.Extensions.find((item) => item.ExtensionName === 'Pictures')?.ExtraFields?.find((item) => item.Title === 'Max Picture Count')?.Value!) || 250
}

export function GetPictureBankUseBackgrounds(settings: SettingsCompany | undefined): boolean {
    if (!settings?.Extensions) { return false }
    return settings.Extensions.find(item => item.ExtensionName === 'Picture Bank')?.ExtraFields?.find(item => item.Title === 'Use background images')?.Value?.toLowerCase() === 'true';
}

export function GetSectionsCheckAll(settings: SettingsCompany): boolean {
    if (!settings.Functions) { return false }
    return settings.Extensions.find(item => item.ExtensionName === 'Sections')?.ExtraFields?.find(item => item.Title === 'Default Check All')?.Value.toLowerCase() === 'true';
}

export function GetSectionsHideIndex(settings: SettingsCompany): boolean {
    if (!settings.Functions) { return false }
    return settings.Extensions.find(item => item.ExtensionName === 'Sections')?.ExtraFields?.find(item => item.Title === 'Default Hide Index')?.Value.toLowerCase() === 'true';
}

export function GetSectionsIgnoreHierarchy(settings: SettingsCompany): boolean {
    if (!settings.Functions) { return false }
    return settings.Extensions.find(item => item.ExtensionName === 'Sections')?.ExtraFields?.find(item => item.Title === 'Default Ignore Hierarchy')?.Value.toLowerCase() === 'true';
}

export function GetSectionsHeadingLevels(settings: SettingsCompany): number[] {
    if (!settings.Extensions) { return [] }
    let levels = settings.Extensions.find(item => item.ExtensionName === 'Sections')?.ExtraFields?.find(item => item.Title === 'Default Paragraph Outline Levels')?.Value || '';
    return levels.split(',').map((item) => { return parseInt(item) });
}

export function GetUseEmailSignature(settings: SettingsCompany): boolean {
    if (!settings.Extensions) { return false }
    const extension = settings.Extensions.find(item => item.ExtensionName === 'Email Signatures');
    return extension?.OutlookDesktop! || extension?.OutlookOnline!;
}

export function GetUseTemplates(settings: SettingsCompany): boolean {
    if (!settings.Extensions) { return false }
    const extension = settings.Extensions.find(item => item.ExtensionName === 'Templates');
    return extension?.PowerPointDesktop! || extension?.WordDesktop! || extension?.ExcelDesktop! || extension?.PowerPointOnline! || extension?.WordOnline! || extension?.ExcelOnline!
}

export function GetUseIcons(settings: SettingsCompany): boolean {
    if (!settings.Extensions) { return false }
    const extension = settings.Extensions.find(item => item.ExtensionName === 'Icons');
    return extension?.PowerPointDesktop! || extension?.WordDesktop! || extension?.ExcelDesktop! || extension?.PowerPointOnline! || extension?.WordOnline! || extension?.ExcelOnline!
}